import { useCallback, useEffect, useState } from "react";
import {
  Input,
  Button,
  Card,
  EmptyState,
  PageHeader,
  FormField,
  PageLayout,
  useToast,
} from "@dawa/ui";
import { User, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/api";

interface Writer {
  id: string;
  name: string;
  expertise?: string | null;
  created_at?: string;
}

const colors = [
  "bg-accent/10 text-accent",
  "bg-ring/10 text-ring",
  "bg-success/10 text-success",
  "bg-warning/10 text-warning",
];
const colorFor = (text: string | null | undefined) => {
  const value = text || "";
  if (!value) return "bg-muted text-muted";
  let sum = 0;
  for (const c of value) sum += c.charCodeAt(0);
  return colors[Math.abs(sum) % colors.length];
};

export default function Writers() {
  const [writers, setWriters] = useState<Writer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", expertise: "" });
  const [formError, setFormError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { name: string; expertise: string }>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { addToast, Toasts } = useToast();

  const load = useCallback(async () => {
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) {
        setWriters([]);
        return;
      }
      const res = await apiFetch("/v1/writers", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const message = body?.error || `Erreur ${res.status}`;
        addToast(message, "error");
        setWriters([]);
        return;
      }
      const data = await res.json();
      setWriters(Array.isArray(data) ? data : []);
    } catch (err) {
      addToast(
        err instanceof Error ? err.message : "Impossible de charger les rédacteurs",
        "error",
      );
      setWriters([]);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <PageLayout maxWidth="full" className="space-y-6">
      <PageHeader
        title="Rédacteurs"
        breadcrumbs={[{ label: "Dashboard", href: "/dashboard" }, { label: "Rédacteurs" }]}
        actions={
          <Button
            className="flex items-center gap-2"
            onClick={() => {
              setShowForm((s) => !s);
              setFormError(null);
            }}
          >
            <Plus className="h-4 w-4" /> Ajouter
          </Button>
        }
      />

      {showForm && (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            void (async () => {
              if (!form.name.trim()) {
                setFormError("Le nom est requis");
                return;
              }
              try {
                setCreating(true);
                const session = await supabase.auth.getSession();
                const token = session.data.session?.access_token;
                if (!token) {
                  setFormError("Session expirée");
                  addToast("Veuillez vous reconnecter", "error");
                  return;
                }
                const res = await apiFetch("/v1/writers", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({
                    name: form.name.trim(),
                    expertise: form.expertise.trim() || undefined,
                  }),
                });
                if (!res.ok) {
                  const body = await res.json().catch(() => ({}));
                  const message = body?.error || `Erreur ${res.status}`;
                  setFormError(message);
                  addToast(message, "error");
                  return;
                }
                const created: Writer = await res.json();
                setWriters((list) => [created, ...list]);
                setForm({ name: "", expertise: "" });
                setShowForm(false);
                setFormError(null);
                addToast("Rédacteur ajouté", "success");
              } catch (err) {
                const message =
                  err instanceof Error ? err.message : "Impossible d'ajouter le rédacteur";
                setFormError(message);
                addToast(message, "error");
              } finally {
                setCreating(false);
              }
            })();
          }}
        >
          <FormField label="Nom" htmlFor="writer-name" required>
            <Input
              id="writer-name"
              placeholder="Nom"
              value={form.name}
              onChange={(e) => {
                setForm({ ...form, name: e.target.value });
                if (formError) setFormError(null);
              }}
            />
          </FormField>
          <FormField label="Expertise" htmlFor="writer-expertise">
            <Input
              id="writer-expertise"
              placeholder="Expertise"
              value={form.expertise}
              onChange={(e) => setForm({ ...form, expertise: e.target.value })}
            />
          </FormField>
          {formError && <p className="text-sm text-danger">{formError}</p>}
          <Button type="submit" loading={creating} disabled={creating}>
            Ajouter
          </Button>
        </form>
      )}

      {!loading && writers.length === 0 ? (
        <EmptyState
          title="Aucun rédacteur"
          description="Ajoutez vos rédacteurs pour mieux organiser la production de contenu."
          action={<Button onClick={() => setShowForm(true)}>Ajouter un rédacteur</Button>}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {writers.map((w) => {
            const draft = drafts[w.id] ?? { name: w.name, expertise: w.expertise ?? "" };
            return (
              <motion.div key={w.id} whileHover={{ scale: 1.02 }}>
                <Card className="flex h-full flex-col justify-between p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/10 text-accent">
                      <User className="h-6 w-6" />
                    </div>
                    <div className="flex-1 space-y-1">
                      {editing === w.id ? (
                        <>
                          <Input
                            value={draft.name}
                            onChange={(e) =>
                              setDrafts((d) => ({
                                ...d,
                                [w.id]: { ...(d[w.id] ?? draft), name: e.target.value },
                              }))
                            }
                          />
                          <Input
                            value={draft.expertise}
                            onChange={(e) =>
                              setDrafts((d) => ({
                                ...d,
                                [w.id]: { ...(d[w.id] ?? draft), expertise: e.target.value },
                              }))
                            }
                          />
                        </>
                      ) : (
                        <>
                          <h3 className="font-semibold">{w.name}</h3>
                          {w.expertise && w.expertise.trim() && (
                            <span
                              className={`inline-block rounded-full px-2 py-1 text-xs ${colorFor(w.expertise)}`}
                            >
                              {w.expertise}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    {editing === w.id ? (
                      <>
                        <Button
                          size="sm"
                          loading={savingId === w.id}
                          disabled={savingId === w.id}
                          onClick={async () => {
                            const payload = {
                              name: (drafts[w.id]?.name ?? draft.name).trim(),
                              expertise: (drafts[w.id]?.expertise ?? draft.expertise).trim(),
                            };
                            if (!payload.name) {
                              addToast("Le nom est requis", "error");
                              return;
                            }
                            try {
                              setSavingId(w.id);
                              const session = await supabase.auth.getSession();
                              const token = session.data.session?.access_token;
                              if (!token) {
                                addToast("Session expirée", "error");
                                return;
                              }
                              const res = await apiFetch(`/v1/writers/${w.id}`, {
                                method: "PATCH",
                                headers: {
                                  "Content-Type": "application/json",
                                  Authorization: `Bearer ${token}`,
                                },
                                body: JSON.stringify({
                                  name: payload.name,
                                  expertise: payload.expertise || undefined,
                                }),
                              });
                              if (!res.ok) {
                                const body = await res.json().catch(() => ({}));
                                const message = body?.error || `Erreur ${res.status}`;
                                addToast(message, "error");
                                return;
                              }
                              const updated: Writer = await res.json();
                              setWriters((list) => list.map((x) => (x.id === w.id ? updated : x)));
                              setEditing(null);
                              setDrafts((d) => {
                                const next = { ...d };
                                delete next[w.id];
                                return next;
                              });
                              addToast("Rédacteur mis à jour", "success");
                            } catch (err) {
                              addToast(
                                err instanceof Error ? err.message : "Impossible de mettre à jour",
                                "error",
                              );
                            } finally {
                              setSavingId(null);
                            }
                          }}
                        >
                          Enregistrer
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setEditing(null);
                            setDrafts((d) => {
                              const next = { ...d };
                              delete next[w.id];
                              return next;
                            });
                          }}
                        >
                          Annuler
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => {
                          setEditing(w.id);
                          setDrafts((d) => ({
                            ...d,
                            [w.id]: { name: w.name, expertise: w.expertise ?? "" },
                          }));
                        }}
                      >
                        Éditer
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="destructive"
                      loading={deletingId === w.id}
                      disabled={deletingId === w.id}
                      onClick={() => {
                        if (!window.confirm("Supprimer ce rédacteur ?")) return;
                        void (async () => {
                          try {
                            setDeletingId(w.id);
                            const session = await supabase.auth.getSession();
                            const token = session.data.session?.access_token;
                            if (!token) {
                              addToast("Session expirée", "error");
                              return;
                            }
                            const res = await apiFetch(`/v1/writers/${w.id}`, {
                              method: "DELETE",
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            if (!res.ok) {
                              const body = await res.json().catch(() => ({}));
                              const message = body?.error || `Erreur ${res.status}`;
                              addToast(message, "error");
                              return;
                            }
                            setWriters((list) => list.filter((x) => x.id !== w.id));
                            addToast("Rédacteur supprimé", "info");
                          } catch (err) {
                            addToast(
                              err instanceof Error ? err.message : "Impossible de supprimer",
                              "error",
                            );
                          } finally {
                            setDeletingId(null);
                          }
                        })();
                      }}
                    >
                      Supprimer
                    </Button>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
      <Toasts />
    </PageLayout>
  );
}
